import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOutIcon, BookText, PenSquare, MoreVertical, Info, UserRound, Mail, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { sendSignInLinkToEmail } from 'firebase/auth';
import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

const Nav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [email, setEmail] = useState('');
  const [signInStatus, setSignInStatus] = useState('initial');
  const sessionId = localStorage.getItem('plottie_session_id');

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setShowSignIn(false);
  }, [location]);

  // Add effect to handle migration when user signs in
  useEffect(() => {
    const migrateUserStories = async () => {
      if (user && sessionId) {
        try {
          const migrateStories = httpsCallable(functions, 'migrateStories');
          await migrateStories({ sessionId });
          console.log('Stories migrated successfully');
        } catch (error) {
          console.error('Error migrating stories:', error);
        }
      }
    };

    migrateUserStories();
  }, [user, sessionId]);

  // Don't show header on landing page
  if (location.pathname === '/') {
    return null;
  }

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleSignInClick = () => {
    setShowSignIn(true);
  };

  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSignInStatus('sending');
    
    const actionCodeSettings = {
      url: `${window.location.origin}${location.pathname}?email=${encodeURIComponent(email)}`,
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      localStorage.setItem('auth_email_for_sign_in', email);
      setSignInStatus('sent');
      setEmail('');
    } catch (error) {
      console.error('Error sending sign-in link', error);
      setSignInStatus('error');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const menuItems = [
    { name: "Geschichte erstellen", icon: PenSquare, href: "/erstellen" },
    { name: "Meine Geschichten", icon: BookText, href: "/meine-geschichten" },
    { name: "FAQ", icon: Info, href: "/faq" },
    // Only show logout if user is authenticated
    ...(user ? [{ name: "Abmelden", icon: LogOutIcon, onClick: handleLogout }] : [])
  ];
  const legalLinks = [
    { name: "Impressum", href: "/impressum" },
    { name: "Datenschutz", href: "/agb#datenschutz" },
  ];

  return (
    <nav className="fixed top-0 z-50 w-full">
      <div 
        className="w-full"
        style={{
          backgroundImage: 'url(/nav-bg.svg)',
          backgroundSize: 'auto 120%',
          backgroundPosition: 'center',
          backgroundRepeat: 'repeat-x',
        }}
      >
        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 xl:px-6">
          <div className="flex items-center justify-between h-16 lg:h-24">
            <Link to="/" className="flex items-center">
              <img 
                src="/Logo.svg" 
                alt="Plottie Logo" 
                className="h-8 lg:h-12 w-auto"
              />
            </Link>

            <div className="relative">
              <motion.button
                onClick={toggleMenu}
                className="flex items-center justify-center focus:outline-none z-50 relative"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                aria-label="Toggle menu"
              >
                <span className="mr-2 text-xl font-medium text-[#343434] font-freeman">
                  Menü
                </span>
                <motion.div
                  animate={{ rotate: isMenuOpen ? 90 : 0 }}
                  transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                >
                  <MoreVertical className="w-6 h-6 text-[#343434]" />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {isMenuOpen && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="fixed inset-0 bg-white/5 backdrop-blur-sm md:hidden"
                      onClick={() => setIsMenuOpen(false)}
                    />
                    <motion.nav
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-16 right-2 space-y-2 w-64"
                    >
                      {menuItems.map((item, index) => (
                        <motion.div
                          key={item.name}
                          initial={{ opacity: 0, x: 50 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: index * 0.05,
                            duration: 0.2,
                          }}
                          style={{ marginLeft: `${index * 0.5}rem` }}
                          className="text-right"
                        >
                          {item.onClick ? (
                            <motion.button
                              onClick={item.onClick}
                              className="inline-flex items-center py-3 px-5 text-lg font-medium text-[#343434] bg-[#FFA23E] rounded-full hover:bg-[#FF9925] transition-colors duration-200 justify-end"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <item.icon className="w-5 h-5 mr-2 text-[#343434]" />
                              {item.name}
                            </motion.button>
                          ) : (
                            <Link to={item.href}>
                              <motion.div
                                className="inline-flex items-center py-3 px-5 text-lg font-medium text-[#343434] bg-[#FFA23E] rounded-full hover:bg-[#FF9925] transition-colors duration-200 justify-end"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <item.icon className="w-5 h-5 mr-2 text-[#343434]" />
                                {item.name}
                              </motion.div>
                            </Link>
                          )}
                        </motion.div>
                      ))}

                      {/* Sign In Section */}
                      {!user && (
                        <motion.div
                          initial={{ opacity: 0, x: 50 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: menuItems.length * 0.05,
                            duration: 0.2,
                          }}
                          style={{ marginLeft: `${menuItems.length * 0.5}rem` }}
                          className="text-right"
                        >
                          <AnimatePresence mode="wait">
                            {signInStatus === "sent" ? (
                              <div className="text-right">
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.9 }}
                                  className="inline-flex items-center py-3 px-5 text-md font-medium text-[#343434] bg-[#FFF3DD] rounded-full whitespace-nowrap"
                                >
                                  <Mail className="w-5 h-5 mr-2" />
                                  <span>Check deine E-Mails!</span>
                                </motion.div>
                              </div>
                            ) : showSignIn ? (
                              <motion.form
                                onSubmit={handleSignInSubmit}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                className="inline-flex items-center gap-1"
                              >
                                <input
                                  type="email"
                                  value={email}
                                  onChange={(e) => setEmail(e.target.value)}
                                  placeholder="Deine Email..."
                                  className="py-3 px-4 text-md font-medium text-[#343434] bg-[#FFF3DD] rounded-full text-left w-[160px]"
                                  disabled={signInStatus === "sending"}
                                />
                                <motion.button
                                  type="submit"
                                  disabled={signInStatus === "sending"}
                                  className="h-[52px] w-[52px] bg-[#FFA23E] text-[#343434] rounded-full hover:bg-[#FF9925] transition-colors duration-200 flex items-center justify-center"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <ArrowRight className="w-5 h-5" />
                                </motion.button>
                              </motion.form>
                            ) : (
                              <motion.button
                                onClick={handleSignInClick}
                                className="inline-flex items-center py-3 px-5 text-lg font-medium text-[#343434] bg-[#FFA23E] rounded-full transition-colors duration-200 justify-end"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <UserRound className="w-5 h-5 mr-2 text-[#343434]" />
                                Anmelden
                              </motion.button>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      )}

                      {/* Legal Links */}
                      <div className="mt-4 flex justify-end space-x-4">
                        {legalLinks.map((link, index) => (
                          <motion.div
                            key={link.name}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{
                              delay: (menuItems.length + index) * 0.05,
                              duration: 0.2,
                            }}
                          >
                            <Link
                              to={link.href}
                              className="text-sm text-[#343434] hover:underline"
                            >
                              {link.name}
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </motion.nav>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Nav; 